import PropTypes from 'prop-types';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { GoogleMap, InfoWindow, useJsApiLoader } from '@react-google-maps/api';

import {
    NoKeyPlaceholder,
    LoadErrorPlaceholder,
    LoadingSpinner,
} from '@/components/common/MapStateShell';
import { geojsonToPaths } from '@/components/common/GeoBoundaryMap';

import { buildPopupHeader, popupAriaLabel } from '../utils/popupContent';

import LayerPopup from './LayerPopup';
import MapLegend from './MapLegend';

import './BiliMap.css';

/** Colorado-centered default view, shown before fitBounds takes over on load. */
const DEFAULT_CENTER = { lat: 39.0, lng: -105.5 };
const DEFAULT_ZOOM = 7;

/**
 * fitBounds zooms in as far as the data allows, which for a single point (or
 * several identical points) means "as far as Google Maps goes" -- much
 * closer than useful. Capping the post-fit zoom keeps a one-point map
 * readable instead of showing a single city block.
 */
const MAX_AUTOFIT_ZOOM = 15;

/**
 * The Google Maps API requires an inline style object for the container.
 * This is the one exception to the no-inline-styles rule (same as
 * GeoBoundaryMap.jsx).
 */
const MAP_CONTAINER_STYLE = {
    width: '100%',
    height: '300px',
    borderRadius: '0.375rem',
};

const MAP_OPTIONS = {
    disableDefaultUI: true,
    zoomControl: true,
    gestureHandling: 'cooperative',
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
};

/**
 * Drawing constants the map document does not send (it resolves colors, not
 * opacity or marker outlines). Client choices, kept in one place.
 */
const POLYGON_FILL_OPACITY = 0.7;
const POLYGON_STROKE_OPACITY = 0.9;
const MARKER_FILL_OPACITY = 0.9;
const MARKER_STROKE_COLOR = '#ffffff';
const MARKER_STROKE_WEIGHT = 1;

/**
 * Markers sharing a zIndex are stacked by Google Maps by screen position
 * (lower on screen in front), not by the order they were created. To keep
 * the draw order normalizeMapDocument() chose (e.g. a proportional layer's
 * small circles on top of large ones), each marker gets its own zIndex:
 * its layer's `z` times this stride, plus its place in the layer. The stride
 * is above the engine's per-layer feature cap, so layers never interleave.
 */
const MARKER_Z_STRIDE = 100000;

/**
 * Computes one bounding box across every feature of every layer, mixing
 * Point features (read directly) with Polygon/MultiPolygon features (via
 * geojsonToPaths, reused from GeoBoundaryMap.jsx). Returns null when there is
 * nothing to bound.
 */
function boundsFromLayers(layers) {
    let minLat = 90;
    let maxLat = -90;
    let minLng = 180;
    let maxLng = -180;
    let touched = false;

    const expand = (lat, lng) => {
        touched = true;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
    };

    layers.forEach((layer) => {
        layer.geometry.features.forEach((feature) => {
            const geom = feature.geometry;
            if (geom.type === 'Point') {
                const [lng, lat] = geom.coordinates;
                expand(lat, lng);
                return;
            }
            geojsonToPaths(geom).forEach((path) =>
                path.forEach((point) => expand(point.lat, point.lng))
            );
        });
    });

    return touched
        ? { north: maxLat, south: minLat, east: maxLng, west: minLng }
        : null;
}

/**
 * Draws the map layers of a v3 map document (already checked by
 * normalizeMapDocument() in utils/mapDocument.js) on one Google Map.
 *
 * Layers arrive sorted by `z` and are drawn in that order (polygons with
 * `zIndex: layer.z`, markers ranked within their layer by MARKER_Z_STRIDE),
 * so area shading sits under points. Every color and size
 * comes from the layer's `drawables`, already resolved from the document's
 * style by normalizeMapDocument(); the client decides nothing about
 * presentation beyond the drawing constants above.
 * Clicking a feature opens a Google Maps info window: its header names the
 * layer and the feature (utils/popupContent.js), its body is a LayerPopup of
 * the fields chosen by the layer's `popup` spec.
 * Under the map: one MapLegend per layer with its data source credit
 * (`attribution`), then each truncated layer's note.
 *
 * Reuses GeoBoundaryMap's useJsApiLoader + no-key/load-error/loading shell
 * (via MapStateShell) and its geojsonToPaths helper.
 */
export default function BiliMap({ layers, title, className }) {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const [map, setMap] = useState(null);
    const [selected, setSelected] = useState(null);
    const overlaysRef = useRef([]);
    const listenersRef = useRef([]);

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: apiKey || '',
    });

    const bounds = useMemo(() => boundsFromLayers(layers), [layers]);

    // The info window header (layer name over the feature's title). Google
    // gives every info window a header row for its close button; without
    // headerContent that row is an empty strip.
    const infoWindowOptions = useMemo(
        () =>
            selected
                ? {
                      headerContent: buildPopupHeader(
                          selected.layer,
                          selected.feature
                      ),
                      ariaLabel: popupAriaLabel(
                          selected.layer,
                          selected.feature
                      ),
                  }
                : null,
        [selected]
    );
    const onMapLoad = useCallback((mapInstance) => {
        setMap(mapInstance);
    }, []);

    // Fit the map to every layer's extent, capping how far a single-point
    // (or all-identical-point) map can zoom in.
    useEffect(() => {
        if (!map || !bounds || !window.google) return undefined;

        map.fitBounds(bounds, { top: 20, bottom: 20, left: 20, right: 20 });
        const listener = window.google.maps.event.addListenerOnce(
            map,
            'bounds_changed',
            () => {
                if (map.getZoom() > MAX_AUTOFIT_ZOOM) {
                    map.setZoom(MAX_AUTOFIT_ZOOM);
                }
            }
        );

        return () => window.google.maps.event.removeListener(listener);
    }, [map, bounds]);

    // Imperatively build one overlay per feature, per layer: Polygon for
    // choropleth, a circle Marker for points/proportional, in the order and
    // with the styles normalizeMapDocument() resolved (`drawables`). Rebuilds from
    // scratch whenever the map instance or the layers change, mirroring
    // DiscoveryMap.jsx's create-in-effect / track-refs / clean-up pattern.
    useEffect(() => {
        if (!map || !window.google) return undefined;

        const clear = () => {
            listenersRef.current.forEach((listener) =>
                window.google.maps.event.removeListener(listener)
            );
            listenersRef.current = [];
            overlaysRef.current.forEach((overlay) => overlay.setMap(null));
            overlaysRef.current = [];
        };
        clear();

        layers.forEach((layer) => {
            const isChoropleth = layer.render_type === 'choropleth';
            layer.drawables.forEach(({ feature, style }, drawIndex) => {
                if (isChoropleth) {
                    const paths = geojsonToPaths(feature.geometry);
                    if (paths.length === 0) return;

                    const polygon = new window.google.maps.Polygon({
                        paths,
                        map,
                        fillColor: style.fillColor,
                        fillOpacity: POLYGON_FILL_OPACITY,
                        strokeColor: style.strokeColor,
                        strokeOpacity: POLYGON_STROKE_OPACITY,
                        strokeWeight: style.strokeWeight,
                        clickable: true,
                        zIndex: layer.z,
                    });
                    listenersRef.current.push(
                        polygon.addListener('click', (event) => {
                            setSelected({
                                layer,
                                feature,
                                position: event.latLng,
                            });
                        })
                    );
                    overlaysRef.current.push(polygon);
                    return;
                }

                const [lng, lat] = feature.geometry.coordinates;
                const position = { lat, lng };
                const marker = new window.google.maps.Marker({
                    position,
                    map,
                    zIndex: layer.z * MARKER_Z_STRIDE + drawIndex,
                    icon: {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: style.radius,
                        fillColor: style.fillColor,
                        fillOpacity: MARKER_FILL_OPACITY,
                        strokeColor: MARKER_STROKE_COLOR,
                        strokeWeight: MARKER_STROKE_WEIGHT,
                    },
                });
                listenersRef.current.push(
                    marker.addListener('click', () => {
                        setSelected({ layer, feature, position });
                    })
                );
                overlaysRef.current.push(marker);
            });
        });

        return clear;
    }, [map, layers]);

    const displayText =
        title || layers.map((layer) => layer.label).join(', ') || 'Map';

    if (!apiKey) {
        return (
            <NoKeyPlaceholder displayText={displayText} className={className} />
        );
    }
    if (loadError) {
        return (
            <LoadErrorPlaceholder
                displayText={displayText}
                className={className}
            />
        );
    }
    if (!isLoaded) {
        return (
            <LoadingSpinner displayText={displayText} className={className} />
        );
    }

    const truncatedLayers = layers.filter(
        (layer) => layer.truncated && layer.note
    );

    return (
        <div className={className || ''}>
            {/* No role="img" wrapper: an interactive map is not one picture.
                Google Maps exposes the map as a labelled region, and the
                enclosing <figure> (MapPayload) is named by the caption. */}
            <GoogleMap
                mapContainerStyle={MAP_CONTAINER_STYLE}
                center={DEFAULT_CENTER}
                zoom={DEFAULT_ZOOM}
                options={MAP_OPTIONS}
                onLoad={onMapLoad}
                onClick={() => setSelected(null)}
            >
                {selected && (
                    <InfoWindow
                        position={selected.position}
                        options={infoWindowOptions}
                        onCloseClick={() => setSelected(null)}
                    >
                        <LayerPopup
                            layer={selected.layer}
                            feature={selected.feature}
                        />
                    </InfoWindow>
                )}
            </GoogleMap>
            <div className="mt-2 d-flex flex-column gap-2">
                {layers.map((layer) => (
                    <div key={layer.id}>
                        <MapLegend
                            layerLabel={layer.label}
                            legend={layer.legend}
                        />
                        {layer.attribution && (
                            <p className="text-muted small mt-1 mb-0">
                                Source: {layer.attribution}
                            </p>
                        )}
                    </div>
                ))}
            </div>
            {truncatedLayers.map((layer) => (
                <p key={layer.id} className="text-muted small mt-2 mb-0">
                    {layer.label}: {layer.note}
                </p>
            ))}
        </div>
    );
}

const layerShape = PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    z: PropTypes.number.isRequired,
    render_type: PropTypes.oneOf(['choropleth', 'points', 'proportional'])
        .isRequired,
    geometry: PropTypes.shape({
        features: PropTypes.arrayOf(
            PropTypes.shape({
                geometry: PropTypes.shape({
                    type: PropTypes.string.isRequired,
                    coordinates: PropTypes.array.isRequired,
                }).isRequired,
                properties: PropTypes.object.isRequired,
            })
        ).isRequired,
    }).isRequired,
    /** Features in draw order, each with its resolved draw style */
    drawables: PropTypes.arrayOf(
        PropTypes.shape({
            feature: PropTypes.object.isRequired,
            style: PropTypes.shape({
                fillColor: PropTypes.string.isRequired,
                strokeColor: PropTypes.string,
                strokeWeight: PropTypes.number,
                radius: PropTypes.number,
            }).isRequired,
        })
    ).isRequired,
    feature_count: PropTypes.number.isRequired,
    style: PropTypes.object.isRequired,
    legend: PropTypes.object.isRequired,
    popup: PropTypes.object,
    attribution: PropTypes.string,
    truncated: PropTypes.bool,
    note: PropTypes.string,
});

BiliMap.propTypes = {
    /** Drawable layers from normalizeMapDocument(), sorted by z */
    layers: PropTypes.arrayOf(layerShape).isRequired,
    /**
     * The map document's title; names the no-key/load-error/loading
     * placeholder card. (The drawn map is named by MapPayload's <figure>.)
     */
    title: PropTypes.string,
    /** Optional CSS class name(s) applied to the wrapper element */
    className: PropTypes.string,
};
